import Vue from "vue";
import Endpoints from "@/share/Endpoints";
import {
  getOrganizationId,
  isAmpiOrganizationUser,
} from "@/common/ampiOrganizationUserAccess";

function formatPermissions(permissions) {
  if (!Array.isArray(permissions)) {
    return permissions && typeof permissions === "object" ? permissions : {};
  }

  const formattedPermissions = {};
  permissions.forEach((permission) => {
    if (!permission || typeof permission !== "object" || !permission.subject) {
      return;
    }

    formattedPermissions[permission.subject] = permission;
  });
  return formattedPermissions;
}
/**
 * Función para iniciar sesión (Aplica para todo tipo de usuario)
 */
export const login = async ({ commit, dispatch }, data) => {
  const validUserStatus = ["VALIDATED", "ACTIVE", "EXPIRED", "INACTIVO", "PROSPECTO", "APPROVED", "TEMPORALLY_LEAVE"]

  try {
    let response = await Vue.axios({
      method: "POST",
      url: "/authentication/login",
      data: {
        email: data.email,
        password: data.password,
      },
    });


    if ( !response?.data?.status ) {
      console.log("ERROR: Storage login");
      return { message: response.data.message, status: false };
    }

    if ( !validUserStatus.includes(response?.data?.user?.validationStatus) ) {
      return {
        message: "El usuario existe.",
        status: true,
        userState: response.data.user.validationStatus,
      };
    }

    localStorage.setItem("USER_TOKEN", response.data.token);
    if (response.data.user.role != "MEMBERSHIP_USER") {
      let permisos = formatPermissions(response.data.user.permissions);
      response.data.user.permissions = permisos;
    }
    await commit("setUser", response.data.user);
    await commit("setToken", response.data.token);
    await dispatch("hydrateAmpiOrganizationContext");

    return {
      message: "Inicio de sesión correcto.",
      status: true,
      userState: response.data.user.validationStatus,
      role: response.data.user.role,
      organization: response.data.user.headOffice
        ? response.data.user.headOffice.id
        : null,
    };

  } catch (err) {
    return err;
  }
};

export const logout = async({ commit }) => {
  try {
    let response = await Vue.axios({
      method: "POST",
      url: "/authentication/logout",
    });

    await commit("logout")
    window.location.href = '/login';
    return {
      message: "Logout exitoso",
      status: true
    }
  } catch (error) {
    return error
  }
}

export const realoadUserData = async ({ commit, dispatch }, data) => {
  try {
    let {
      data: { user, status },
    } = await Vue.axios({
      method: "POST",
      url: "/authentication/reload-user-data",
      data: {
        email: data.email,
        id: data.id,
      },
    });
    if (status) {
      if (
        user.validationStatus == "VALIDATED" ||
        user.validationStatus == "ACTIVE" ||
        user.validationStatus == "EXPIRED" ||
        user.validationStatus == "INACTIVO" ||
        user.validationStatus == "PROSPECTO"
      ) {
        user.permissions = formatPermissions(user.permissions);
        await commit("setUser", user);
        await dispatch("hydrateAmpiOrganizationContext");
        return {
          message: "Inicio de sesión correcto.",
          status: true,
          userState: user.validationStatus,
          role: user.role,
        };
      } else {
        return {
          message: "El usuario existe.",
          status: true,
          userState: user.validationStatus,
        };
      }
    } else {
      return { message: data.message, status: false };
    }
  } catch (err) {
    return err;
  }
};

export const botTemporalAccess = async ({ commit }, data) => {
  try {
    let {
      data: { token, status, user },
    } = await Vue.axios({
      method: "POST",
      url: "/authentication/bot-temporal-access",
      headers: {
        Authorization: `Bearer ${data.token}`,
      },
      data: {
        id: data.id,
      },
    });

    if (status) {
      localStorage.setItem("USER_TOKEN", token);
      await commit("setToken", token);
      return { message: "Acceso temporal concedido.", status: true };
    } else {
      return { message: data.message, status: false };
    }
  } catch (err) {
    return err;
  }
};

export const refreshToken = async ({ commit }, data) => {
  try {
    let {
      data: { token, status },
    } = await Vue.axios({
      method: "POST",
      url: "/authentication/refresh-token",
      data: {
        email: data.email,
      },
    });
    if (status && status != 403) {
      if (token) {
        localStorage.setItem("USER_TOKEN", token);
        await commit("setToken", token);
        return token;
      } else {
        throw new Error("La respuesta de renovación no contiene token");
      }
    } else {
      throw new Error("La sesión no pudo renovarse");
    }
  } catch (err) {
    // El consumidor decide si una renovación fallida debe cerrar la sesión.
    // Votaciones comparte esta acción con el resto de la plataforma y no
    // debe borrar la sesión global solo porque falló una petición aislada.
    throw err;
  }
};

export const fetchUserInformation = async ({ commit, dispatch }, data) => {
  try {
    let { data } = await Vue.axios({
      method: "GET",
      url: "/users",
    });
    let permisos = formatPermissions(data.user.permissions);
    data.user.permissions = permisos;
    commit("setUser", data.user);
    await dispatch("hydrateAmpiOrganizationContext");
    return {
      status: true,
      message: "Información consultada correctamente",
      user: data.user,
    };
  } catch (error) {
    return { status: false, message: error.message };
  }
};

/**
 * Loads only the organization settings needed by the legacy dashboard for an
 * AMPI delegated administrator. The parent user is never merged into the
 * authenticated user's identity.
 */
export const hydrateAmpiOrganizationContext = async ({ state, commit }) => {
  const user = state && state.user;
  if (!isAmpiOrganizationUser(user)) {
    return { status: false, message: "El usuario no es un AMPI ORGANIZATION_USER" };
  }

  const organizationId = getOrganizationId({
    organization: user.organization,
    headOffice: user.headOffice,
  });
  if (!organizationId) {
    return { status: false, message: "No se encontró la organización padre" };
  }

  try {
    const response = await Vue.axios({
      url: `/users/${organizationId}`,
      method: "GET",
    });
    const organization = response?.data?.user || response?.data;
    if (!organization) {
      return { status: false, message: "Respuesta vacía de la organización padre" };
    }

    const context = {
      _id: organization._id || organization.id || organizationId,
      role: organization.role,
      hierarchyType: organization.hierarchyType,
      allowSubmembers: organization.allowSubmembers,
      allowBillingModule: organization.allowBillingModule,
      canApproveMembers: organization.canApproveMembers,
      paymentType: organization.paymentType,
    };

    commit("setOrganizationContext", context);
    return { status: true, context };
  } catch (error) {
    console.warn(
      "[organization-user-access] could not hydrate AMPI organization context",
      error
    );
    return {
      status: false,
      message: error?.message || "No se pudo cargar la configuración de la organización",
    };
  }
};

/**
 * Obtiene la imagen de la organización asociada a un ORGANIZATION_USER.
 * Se mantiene fuera de la respuesta de autenticación para no cambiar su
 * estructura ni sobrescribir el profilePicture propio del usuario.
 */
export const fetchOrganizationProfilePicture = async ({ commit, state }) => {
  if (!state.user || state.user.role !== "ORGANIZATION_USER") {
    return { status: false, message: "El usuario no es ORGANIZATION_USER" };
  }

  try {
    const { data } = await Vue.axios({
      method: "GET",
      url: Endpoints.usersOrganizationProfilePicture,
    });
    const profilePicture = data && data.profilePicture ? data.profilePicture : "";
    commit("setOrganizationProfilePicture", profilePicture);
    return {
      status: true,
      message: "Imagen de la organización consultada correctamente",
      profilePicture,
    };
  } catch (error) {
    commit("setOrganizationProfilePicture", "");
    return {
      status: false,
      message:
        (error && error.response && error.response.data &&
          (error.response.data.error?.message || error.response.data.message)) ||
        (error && error.message) ||
        "No se pudo consultar la imagen de la organización",
    };
  }
};

/**
 * Refresca los datos del usuario autenticado (campos estáticos del
 * perfil + memberData) usando el endpoint dedicado
 * GET /users/profile-data. Pensado para llamarlo justo después de
 * un guardado en la vista member-v2/profile, así la próxima vez que
 * la app lea state.user va a tener los valores persistidos en vez
 * del snapshot viejo cargado al inicio de la sesión.
 *
 * El resultado se commitea a `setUser` y, además, se persiste en
 * localStorage bajo la clave `USER_PROFILE` para que sobreviva a un
 * refresh del navegador.
 */
export const fetchProfileData = async ({ commit, state }) => {
  try {
    const { data } = await Vue.axios({
      method: "GET",
      url: "/users/profile-data",
    });
    if (data && data.user) {
      const currentUser = state && state.user ? state.user : null;
      const refreshedUser = currentUser ? { ...currentUser, ...data.user } : data.user;
      await commit("setUser", refreshedUser);
      try {
        localStorage.setItem("USER_PROFILE", JSON.stringify(refreshedUser));
      } catch (storageErr) {
        console.warn(
          "[v2-profile] could not persist USER_PROFILE to localStorage",
          storageErr
        );
      }
      return {
        status: true,
        message: "Perfil refrescado correctamente",
        user: refreshedUser,
      };
    }
    return { status: false, message: "Respuesta vacía del servidor" };
  } catch (error) {
    console.log("[v2-profile] fetchProfileData ERROR", error);
    return {
      status: false,
      message:
        (error && error.response && error.response.data &&
          (error.response.data.error?.message || error.response.data.message)) ||
        (error && error.message) ||
        "No se pudo refrescar el perfil",
    };
  }
};

export const validateUser = async ({ commit }, email) => {
  try {
    let { data } = await Vue.axios({
      method: "post",
      url: "/users/validateUser",
      data: {
        email: email,
      },
    });
    return data;
  } catch (error) {
    return error;
  }
};

export const createOrganization = async ({ commit }, userData) => {
  try {
    console.log(userData);
    let {
      data: { user, token },
    } = await Vue.axios({
      method: "POST",
      url: "/users/create",
      data: {
        currentLicense: userData.currentLicense,
        company: userData.company,
        email: userData.email,
        firstLastname: userData.firstLastname,
        secondLastname: userData.secondLastname,
        name: userData.name,
        password: userData.password,
        role: "Organization",
        validationStatus: "ToValidate",
      },
    });
    return {
      status: true,
      message:
        "¡Listo! Has creado una cuenta en WeChamber. Revisa la bandeja de entrada de tu correo electrónico y valida tu cuenta para comenzar.",
      user: user,
    };
  } catch (err) {
    return {
      status: false,
      message: "Ha ocurrido un problema al registrar el usuario",
      user: err,
    };
  }
};

export const getIndicators = async ({ commit }, data) => {
  try {
    let { data } = await Vue.axios({
      method: "GET",
      url: "/users/credits-indicators",
    });
    commit("setIndicators", data.indicators);
    return {
      status: true,
      message: "Información consultada correctamente",
      indicators: data,
    };
  } catch (error) {
    return { status: false, message: error.message };
  }
};
